<template>
  <section>
    <p>
      Shops that are not on ADAS Map. These are the customers a provider uploads estimates for: saving one
      here means it can be picked on the Upload Estimate screen instead of being retyped.
    </p>

    <div v-if="errorMessage" class="alert alert-warning py-2">{{ errorMessage }}</div>

    <div class="d-flex align-items-center gap-2 mb-2">
      <input
        v-model.trim="searchTerm"
        class="form-control form-control-sm custom-search"
        placeholder="search by name, contact or email"
        :disabled="loading"
        @keyup.enter="load"
      />
      <button class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="load">
        <i class="fas fa-sync me-1"></i>{{ loading ? 'Loading…' : 'Refresh' }}
      </button>
      <button class="btn btn-primary btn-sm ms-auto" :disabled="loading || !!editing" @click="add">
        <i class="fas fa-plus me-1"></i>Add Shop
      </button>
    </div>

    <!-- The form replaces the table rather than sitting beside it: these are short records and the
         locations pane is already narrow. -->
    <div v-if="editing" class="custom-editor p-2 mb-2">
      <div class="row g-2">
        <div class="col-6">
          <label class="custom-label">Shop Name <span class="text-danger">*</span></label>
          <input v-model="editing.shopName" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div class="col-6">
          <label class="custom-label">Contact Name</label>
          <input v-model="editing.contactName" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div class="col-6">
          <label class="custom-label">Customer Email</label>
          <input v-model="editing.email" class="form-control form-control-sm" type="email" :disabled="saving" />
        </div>
        <div class="col-6">
          <label class="custom-label">Phone</label>
          <input v-model="editing.phone" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div class="col-12">
          <label class="custom-label">Address</label>
          <input v-model="editing.address1" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div class="col-5">
          <label class="custom-label">City</label>
          <input v-model="editing.city" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div class="col-3">
          <label class="custom-label">State</label>
          <input v-model="editing.stateProvince" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div class="col-4">
          <label class="custom-label">Postal Code</label>
          <input v-model="editing.postalCode" class="form-control form-control-sm" :disabled="saving" />
        </div>
        <div v-if="formError" class="col-12 custom-field-error">{{ formError }}</div>
        <div class="col-12 text-end">
          <button class="btn btn-secondary btn-sm me-1" :disabled="saving" @click="cancel">Cancel</button>
          <button class="btn btn-primary btn-sm" :disabled="saving" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Save
          </button>
        </div>
      </div>
    </div>

    <table class="table table-sm table-bordered table-striped">
      <thead>
        <tr>
          <th>Shop Name</th><th>Contact Name</th><th>Customer Email</th><th>Phone</th><th>Location</th><th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="shop in shops" :key="shop.customerShop">
          <td>{{ shop.shopName }}</td>
          <td>{{ shop.contactName }}</td>
          <td>{{ shop.email }}</td>
          <td>{{ shop.phone }}</td>
          <td>{{ place(shop) }}</td>
          <td class="text-center custom-actions">
            <a href="#" title="Edit this shop" @click.prevent="edit(shop)"><i class="fas fa-pen"></i></a>
            <a href="#" title="Remove this shop" @click.prevent="remove(shop)"><i class="fas fa-trash"></i></a>
          </td>
        </tr>
        <tr v-if="!shops.length && !loading">
          <td colspan="6" class="text-center text-muted">No shops saved yet.</td>
        </tr>
      </tbody>
    </table>

    <DialogConfirm
      v-if="confirming"
      :model-value="true"
      confirm-title="Remove Shop"
      confirm-action="Remove"
      :confirm-question="`Remove ${confirming.shopName}? Inspections already linked to it keep their details.`"
      @closing="confirming = null"
      @proceed="confirmRemove"
    />
  </section>
</template>
<script>
import { mapActions } from 'pinia'
import { customerStore } from '@/stores/customer'
import DialogConfirm from '@/components/utilities/elements/ConfirmDialog.vue'
export default {
  name: 'LocationCustomerShops',
  components: { DialogConfirm },
  props: {
    firm: { type: [Number, String], default: null }
  },
  data() {
    return {
      shops: [],
      loading: false,
      saving: false,
      searchTerm: '',
      editing: null,
      confirming: null,
      errorMessage: '',
      formError: ''
    }
  },
  watch: {
    firm() {
      this.cancel()
      this.load()
    }
  },
  async mounted() {
    await this.load()
  },
  methods: {
    ...mapActions(customerStore, ['getShops', 'saveShop']),
    place(shop) {
      return [shop.city, shop.stateProvince].filter((p) => p).join(', ')
    },
    async load() {
      this.loading = true
      this.errorMessage = ''
      try {
        this.shops = await this.getShops(this.searchTerm || null, this.firm || null)
      } catch (e) {
        const d = e && e.response && e.response.data
        this.errorMessage = (d && ((d.error && d.error.message) || d.message || d.Message)) || 'Failed to load customer shops.'
        this.shops = []
      } finally {
        this.loading = false
      }
    },
    add() {
      this.formError = ''
      this.editing = { customerShop: null, shopName: '', contactName: '', email: '', phone: '', address1: '', city: '', stateProvince: '', postalCode: '' }
    },
    edit(shop) {
      this.formError = ''
      this.editing = { ...shop }
    },
    cancel() {
      this.editing = null
      this.formError = ''
    },
    async save() {
      if (!this.editing.shopName || !this.editing.shopName.trim()) {
        this.formError = 'A shop name is required.'
        return
      }
      this.saving = true
      try {
        await this.saveShop({ ...this.editing, firm: this.firm || undefined })
        this.editing = null
        await this.load()
      } catch (e) {
        const d = e && e.response && e.response.data
        this.formError = (d && ((d.error && d.error.message) || d.message || d.Message)) || 'Failed to save the shop.'
      } finally {
        this.saving = false
      }
    },
    remove(shop) {
      this.confirming = shop
    },
    // Marked inactive rather than deleted: an inspection already linked to this shop still has to be
    // able to show who it was for.
    async confirmRemove() {
      const shop = this.confirming
      this.confirming = null
      if (!shop) return
      try {
        await this.saveShop({ ...shop, isInactive: true, firm: this.firm || undefined })
        await this.load()
      } catch (e) {
        const d = e && e.response && e.response.data
        this.errorMessage = (d && ((d.error && d.error.message) || d.message || d.Message)) || 'Failed to remove the shop.'
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
}
.custom-search {
  width: 320px;
}
.custom-editor {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #f8f9fa;
}
.custom-field-error {
  color: #dc3545;
  font-size: 0.8rem;
}
.custom-actions a {
  margin: 0 6px;
}
</style>
